/**
 * @description Pack SO company dropdown (LYN-357)
 *
 * Key features:
 * - All | granted companies | default-on-login | + Add company
 * - All is UI-only — never written as X-Company-Id
 * - Default persists via PATCH /auth/default-company, not localStorage
 *
 * @dependencies AuthContext, PackChromeContext
 *
 * @notes Light rail styles (LYN-360). Does not touch LynxPay CompanySwitcher.
 *   Footer is the person, not this. All still calls switchCompany(null).
 */

import React, { useEffect, useRef, useState } from 'react';
import { Building2, Check, ChevronDown, Loader2, Plus, Star } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { usePackChrome } from '../../../contexts/PackChromeContext';

const PackCompanySwitcher = ({ isSidebarOpen, isMobile = false }) => {
  const {
    companies,
    activeCompanyId,
    companyScope,
    defaultCompanyScope,
    defaultCompanyId,
    switchCompany,
    setDefaultCompany
  } = useAuth();
  const { openAddCompany } = usePackChrome();
  const [isOpen, setIsOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const dropdownRef = useRef(null);

  const isAll = companyScope === 'all' || !activeCompanyId;
  const active = companies.find((row) => row.id === activeCompanyId) || null;
  const displayName = isAll ? 'All' : (active?.businessName || 'Select company');
  const showLabel = isMobile || isSidebarOpen;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleSelect = async (nextId) => {
    if (isSwitching) return;
    setIsSwitching(true);
    try {
      await switchCompany(nextId);
    } finally {
      setIsSwitching(false);
      setIsOpen(false);
    }
  };

  const handleDefault = async (event, scope, companyId = null) => {
    event.stopPropagation();
    await setDefaultCompany({ scope, companyId });
  };

  const allIsDefault = defaultCompanyScope === 'all';

  return (
    <div className="relative mb-3 pb-3 border-b border-gray-200" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => !isSwitching && setIsOpen(!isOpen)}
        className="w-full flex items-center py-2 rounded-lg transition-colors duration-200 hover:bg-gray-50 cursor-pointer"
        disabled={isSwitching}
        aria-label="Company menu"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <div className="w-10 flex items-center justify-center flex-shrink-0">
          {isSwitching ? (
            <Loader2 className="w-5 h-5 text-brand animate-spin" />
          ) : (
            <Building2 className="w-5 h-5 text-brand" />
          )}
        </div>
        <div className={`flex-1 flex items-center justify-between overflow-hidden transition-all duration-300 ${
          showLabel ? 'opacity-100 max-w-full delay-[50ms]' : 'opacity-0 max-w-0'
        }`}>
          <div className="min-w-0 text-left">
            <div className="text-sm font-semibold text-gray-900 truncate pr-1">{displayName}</div>
          </div>
          <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 mr-2 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`} />
        </div>
      </button>

      {isOpen && (
        <div
          className={`absolute z-50 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg py-1 min-w-[220px] ${
            !isMobile && !isSidebarOpen ? 'left-14 top-0' : 'left-0 right-0'
          }`}
          role="listbox"
          aria-label="Select company"
        >
          <div className="relative">
            <button
              type="button"
              onClick={() => handleSelect(null)}
              role="option"
              aria-selected={isAll}
              className={`w-full flex items-center px-3 py-2.5 text-left transition-colors ${
                isAll ? 'bg-gray-100 text-gray-900' : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <span className="flex-1 text-sm font-medium">All</span>
              {isAll && <Check className="w-4 h-4 text-brand flex-shrink-0" />}
            </button>
            <button
              type="button"
              onClick={(event) => handleDefault(event, 'all')}
              className="absolute right-8 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-brand"
              aria-label="Default on login: All"
            >
              <Star className={`w-3.5 h-3.5 ${allIsDefault ? 'fill-brand text-brand' : ''}`} />
            </button>
          </div>

          {companies.map((row) => {
            const selected = !isAll && row.id === activeCompanyId;
            const isDefault = defaultCompanyScope === 'company' && defaultCompanyId === row.id;
            return (
              <div key={row.id} className="relative">
                <button
                  type="button"
                  onClick={() => handleSelect(row.id)}
                  role="option"
                  aria-selected={selected}
                  className={`w-full flex items-center px-3 py-2.5 pr-16 text-left transition-colors ${
                    selected ? 'bg-gray-100 text-gray-900' : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <span className="flex-1 text-sm font-medium truncate">{row.businessName}</span>
                  {selected && <Check className="w-4 h-4 text-brand flex-shrink-0" />}
                </button>
                <button
                  type="button"
                  onClick={(event) => handleDefault(event, 'company', row.id)}
                  className="absolute right-8 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-brand"
                  aria-label={`Default on login: ${row.businessName}`}
                >
                  <Star className={`w-3.5 h-3.5 ${isDefault ? 'fill-brand text-brand' : ''}`} />
                </button>
              </div>
            );
          })}

          <div className={`${companies.length > 0 ? 'border-t border-gray-200 mt-1 pt-1' : ''}`}>
            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                openAddCompany();
              }}
              className="w-full flex items-center px-3 py-2 text-left text-gray-600 hover:bg-gray-50 hover:text-gray-900 transition-colors"
            >
              <Plus className="w-4 h-4 mr-2 flex-shrink-0" />
              <span className="text-sm">Add company</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PackCompanySwitcher;
